import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { supabase } from "../lib/supabase";
import { sendEmail } from "../lib/email-helper";
import { Download, Mail, TrendingUp, Globe, MousePointer, Users, Clock, BarChart3, ArrowLeft } from "lucide-react";

interface VisitRow {
  id: string;
  session_id: string;
  page_path: string;
  page_title: string | null;
  referrer: string | null;
  user_agent: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  device_type: string | null;
  browser: string | null;
  operating_system: string | null;
  created_at: string;
}

function BarChart({ data, color = "var(--accent-500)", height = 180 }: { data: { label: string; value: number }[]; color?: string; height?: number }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "0.375rem", height, padding: "0.5rem 0" }}>
      {data.map((d, i) => {
        const pct = (d.value / max) * 100;
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem", minWidth: 0 }}>
            <div style={{ fontSize: "0.625rem", color: "var(--text-muted)", fontWeight: 600 }}>{d.value}</div>
            <div style={{ width: "100%", maxWidth: "32px", height: `${pct}%`, background: color, borderRadius: "var(--radius-sm) var(--radius-sm) 0 0", minHeight: d.value > 0 ? "4px" : "0", transition: "height 0.4s ease" }} />
            <div style={{ fontSize: "0.5625rem", color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%", textAlign: "center" }}>{d.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function LineChart({ data, height = 200 }: { data: { label: string; value: number }[]; height?: number }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  const points = data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * 100;
    const y = 100 - (d.value / max) * 100;
    return `${x},${y}`;
  });
  const areaPoints = `0,100 ${points.join(" ")} 100,100`;
  return (
    <div style={{ position: "relative", height, padding: "0.5rem 0" }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: "100%", overflow: "visible" }}>
        <polygon points={areaPoints} fill="var(--accent-100)" opacity="0.6" />
        <polyline points={points.join(" ")} fill="none" stroke="var(--accent-500)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {data.map((d, i) => {
          const x = (i / Math.max(data.length - 1, 1)) * 100;
          const y = 100 - (d.value / max) * 100;
          return <circle key={i} cx={x} cy={y} r="1.5" fill="var(--accent-600)" vectorEffect="non-scaling-stroke" />;
        })}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.25rem" }}>
        {data.map((d, i) => (
          <div key={i} style={{ fontSize: "0.5625rem", color: "var(--text-muted)", flex: 1, textAlign: "center" }}>{d.label}</div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sublabel }: { icon: React.ReactNode; label: string; value: string; sublabel?: string }) {
  return (
    <div className="card" style={{ padding: "var(--space-4)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
        <div style={{ width: "36px", height: "36px", borderRadius: "var(--radius-md)", background: "var(--accent-50)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--accent-600)" }}>
          {icon}
        </div>
        <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--neutral-500)" }}>{label}</div>
      </div>
      <div style={{ fontSize: "1.75rem", fontWeight: 800, marginBottom: "0.25rem" }}>{value}</div>
      {sublabel && <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{sublabel}</div>}
    </div>
  );
}

function DonutChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <p className="text-muted" style={{ fontSize: "0.875rem" }}>No data yet.</p>;
  let offset = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
      <svg viewBox="0 0 42 42" style={{ width: "120px", height: "120px", flexShrink: 0 }}>
        <circle cx="21" cy="21" r="15.915" fill="none" stroke="var(--neutral-100)" strokeWidth="6" />
        {data.map((d, i) => {
          const pct = (d.value / total) * 100;
          const dash = `${pct} ${100 - pct}`;
          const el = (
            <circle key={i} cx="21" cy="21" r="15.915" fill="none" stroke={d.color} strokeWidth="6" strokeDasharray={dash} strokeDashoffset={-offset} transform="rotate(-90 21 21)" style={{ transition: "stroke-dasharray 0.4s ease" }} />
          );
          offset += pct;
          return el;
        })}
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
        {data.map((d) => (
          <div key={d.label} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "var(--radius-sm)", background: d.color, flexShrink: 0 }} />
            <span style={{ fontWeight: 500 }}>{d.label}</span>
            <span style={{ color: "var(--text-muted)", marginLeft: "auto" }}>{d.value} ({Math.round((d.value / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SeoAnalyticsPage() {
  const navigate = useNavigate();
  const [visits, setVisits] = useState<VisitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<"7d" | "30d" | "90d" | "all">("30d");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);

  useEffect(() => {
    const days = dateRange === "7d" ? 7 : dateRange === "30d" ? 30 : dateRange === "90d" ? 90 : 9999;
    const since = new Date(Date.now() - days * 86400000).toISOString();

    supabase
      .from("sentinel_visitor_tracking")
      .select("id, session_id, page_path, page_title, referrer, user_agent, country, region, city, device_type, browser, operating_system, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5000)
      .then(({ data, error }) => {
        if (error) {
          console.error("Analytics fetch error:", error);
        }
        if (data) setVisits(data as VisitRow[]);
        setLoading(false);
      });
  }, [dateRange]);

  const totalVisits = visits.length;
  const uniqueSessions = new Set(visits.map((v) => v.session_id)).size;
  const uniquePages = new Set(visits.map((v) => v.page_path)).size;
  const uniqueCountries = new Set(visits.map((v) => v.country).filter(Boolean)).size;

  // Daily traffic data (last N days)
  const days = dateRange === "7d" ? 7 : dateRange === "30d" ? 30 : dateRange === "90d" ? 90 : 30;
  const dailyMap: Record<string, number> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000);
    const key = `${d.getMonth() + 1}/${d.getDate()}`;
    dailyMap[key] = 0;
  }
  visits.forEach((v) => {
    const d = new Date(v.created_at);
    const key = `${d.getMonth() + 1}/${d.getDate()}`;
    if (key in dailyMap) dailyMap[key]++;
  });
  const dailyData = Object.entries(dailyMap).map(([label, value]) => ({ label, value }));

  // Top pages
  const pageMap: Record<string, number> = {};
  visits.forEach((v) => { pageMap[v.page_path] = (pageMap[v.page_path] ?? 0) + 1; });
  const topPages = Object.entries(pageMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 10);

  // Referrers
  const refMap: Record<string, number> = {};
  visits.forEach((v) => {
    const ref = v.referrer || "(direct)";
    let label = ref;
    try { if (ref !== "(direct)") label = new URL(ref).hostname; } catch { label = ref; }
    refMap[label] = (refMap[label] ?? 0) + 1;
  });
  const topReferrers = Object.entries(refMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 8);

  // Countries
  const countryMap: Record<string, number> = {};
  visits.forEach((v) => {
    const c = v.country || "Unknown";
    countryMap[c] = (countryMap[c] ?? 0) + 1;
  });
  const topCountries = Object.entries(countryMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 10);

  // Device breakdown
  const deviceMap: Record<string, number> = { desktop: 0, mobile: 0, tablet: 0 };
  visits.forEach((v) => { if (v.device_type) deviceMap[v.device_type] = (deviceMap[v.device_type] ?? 0) + 1; });
  const deviceData = Object.entries(deviceMap).filter(([, v]) => v > 0).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1),
    value,
    color: label === "desktop" ? "var(--accent-500)" : label === "mobile" ? "#0891b2" : "#d97706",
  }));

  // Browser breakdown
  const browserMap: Record<string, number> = {};
  visits.forEach((v) => { if (v.browser) browserMap[v.browser] = (browserMap[v.browser] ?? 0) + 1; });
  const browserList = Object.entries(browserMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  const browserColors = ["var(--accent-500)", "#0891b2", "#d97706", "#059669", "#7c3aed", "#6b7280"];
  const browserData = browserList.slice(0, 6).map((d, i) => ({ ...d, color: browserColors[i] }));

  // OS breakdown
  const osMap: Record<string, number> = {};
  visits.forEach((v) => { if (v.operating_system) osMap[v.operating_system] = (osMap[v.operating_system] ?? 0) + 1; });
  const osList = Object.entries(osMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);

  // Hourly traffic
  const hourlyMap: Record<number, number> = {};
  for (let i = 0; i < 24; i++) hourlyMap[i] = 0;
  visits.forEach((v) => { const h = new Date(v.created_at).getHours(); hourlyMap[h]++; });
  const hourlyData = Object.entries(hourlyMap).map(([k, value]) => ({ label: k, value }));

  const handleDownloadCsv = useCallback(() => {
    const headers = ["Date", "Session ID", "Page Path", "Page Title", "Referrer", "Country", "Region", "City", "Device", "Browser", "OS"];
    const rows = visits.map((v) => [
      new Date(v.created_at).toISOString(),
      v.session_id,
      v.page_path,
      v.page_title ?? "",
      v.referrer ?? "",
      v.country ?? "",
      v.region ?? "",
      v.city ?? "",
      v.device_type ?? "",
      v.browser ?? "",
      v.operating_system ?? "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sentinel-analytics-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [visits]);

  const handleEmailReport = useCallback(async () => {
    setSendingEmail(true);
    setEmailStatus(null);
    try {
      const html = `
<h2>Sentinel SEO Analytics Report</h2>
<p>Generated: ${new Date().toLocaleString()}</p>
<p>Date range: ${dateRange === "all" ? "All time" : `Last ${dateRange}`}</p>

<h3>Summary</h3>
<ul>
<li>Total Visits: <strong>${totalVisits}</strong></li>
<li>Unique Sessions: <strong>${uniqueSessions}</strong></li>
<li>Unique Pages: <strong>${uniquePages}</strong></li>
<li>Countries: <strong>${uniqueCountries}</strong></li>
</ul>

<h3>Top Pages</h3>
<ul>${topPages.map((p) => `<li>${p.label}: ${p.value} visits</li>`).join("")}</ul>

<h3>Top Referrers</h3>
<ul>${topReferrers.map((r) => `<li>${r.label}: ${r.value}</li>`).join("")}</ul>

<h3>Top Countries</h3>
<ul>${topCountries.map((c) => `<li>${c.label}: ${c.value}</li>`).join("")}</ul>

<h3>Device Breakdown</h3>
<ul>${deviceData.map((d) => `<li>${d.label}: ${d.value} (${Math.round((d.value / totalVisits) * 100)}%)</li>`).join("")}</ul>

<h3>Browser Breakdown</h3>
<ul>${browserList.map((b) => `<li>${b.label}: ${b.value}</li>`).join("")}</ul>

<h3>Operating Systems</h3>
<ul>${osList.map((o) => `<li>${o.label}: ${o.value}</li>`).join("")}</ul>
`;

      await sendEmail({
        to: "brian.smith@sentinel-architecture.com",
        cc: "brian.smith@e-industries.net",
        subject: `Sentinel SEO Analytics Report — ${new Date().toLocaleDateString()}`,
        body: html,
        email_type: "analytics_report",
      });
      setEmailStatus("Report sent to brian.smith@sentinel-architecture.com");
    } catch {
      setEmailStatus("Failed to send report. Please try again.");
    }
    setSendingEmail(false);
  }, [totalVisits, uniqueSessions, uniquePages, uniqueCountries, topPages, topReferrers, topCountries, deviceData, browserList, osList, dateRange]);

  if (loading) {
    return (
      <PlatformLayout>
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading analytics...</div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "var(--space-4)", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <button onClick={() => navigate("/admin")} style={{ background: "transparent", border: "none", color: "var(--text-muted)", fontSize: "0.8125rem", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.375rem", marginBottom: "0.5rem" }}>
            <ArrowLeft size={14} /> Back to Admin
          </button>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.25rem" }}>SEO Analytics Dashboard</h1>
          <p className="text-muted" style={{ fontSize: "0.875rem" }}>Website traffic, visitor geography, and engagement metrics</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {(["7d", "30d", "90d", "all"] as const).map((r) => (
            <button key={r} onClick={() => setDateRange(r)} className="btn" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", background: dateRange === r ? "var(--accent-500)" : "var(--neutral-100)", color: dateRange === r ? "#fff" : "var(--text-muted)", border: "none", cursor: "pointer", fontWeight: 600, borderRadius: "var(--radius-sm)" }}>
              {r === "all" ? "All Time" : `Last ${r}`}
            </button>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "var(--space-4)" }}>
        <button onClick={handleDownloadCsv} className="btn btn-secondary" style={{ fontSize: "0.8125rem", display: "flex", alignItems: "center", gap: "0.375rem" }}>
          <Download size={14} /> Download CSV
        </button>
        <button onClick={handleEmailReport} disabled={sendingEmail} className="btn btn-secondary" style={{ fontSize: "0.8125rem", display: "flex", alignItems: "center", gap: "0.375rem" }}>
          <Mail size={14} /> {sendingEmail ? "Sending..." : "Email Report"}
        </button>
        {emailStatus && <span style={{ fontSize: "0.8125rem", color: emailStatus.includes("Failed") ? "var(--error-600)" : "var(--accent-600)", fontWeight: 600, alignSelf: "center" }}>{emailStatus}</span>}
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--space-3)", marginBottom: "var(--space-4)" }}>
        <StatCard icon={<MousePointer size={18} />} label="Total Visits" value={totalVisits.toLocaleString()} sublabel={`${dateRange === "all" ? "All time" : `Last ${dateRange}`}`} />
        <StatCard icon={<Users size={18} />} label="Unique Visitors" value={uniqueSessions.toLocaleString()} sublabel="By session" />
        <StatCard icon={<BarChart3 size={18} />} label="Pages Viewed" value={uniquePages.toLocaleString()} sublabel="Unique pages" />
        <StatCard icon={<Globe size={18} />} label="Countries" value={uniqueCountries.toLocaleString()} sublabel="Visitor locations" />
        <StatCard icon={<Clock size={18} />} label="Avg Per Session" value={uniqueSessions > 0 ? (totalVisits / uniqueSessions).toFixed(1) : "0"} sublabel="Pages per visitor" />
        <StatCard icon={<TrendingUp size={18} />} label="Daily Avg" value={days > 0 ? Math.round(totalVisits / days).toLocaleString() : "0"} sublabel="Visits per day" />
      </div>

      {/* Traffic Over Time */}
      <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-4)" }}>
        <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Daily Traffic</h2>
        {totalVisits === 0 ? (
          <p className="text-muted" style={{ fontSize: "0.875rem", textAlign: "center", padding: "2rem" }}>No visitor data yet for this period. As people visit your site, traffic will appear here.</p>
        ) : dailyData.length <= 14 ? (
          <BarChart data={dailyData} />
        ) : (
          <LineChart data={dailyData} />
        )}
      </div>

      {/* Two-column layout */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "var(--space-4)", marginBottom: "var(--space-4)" }}>
        {/* Top Pages */}
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Top Pages</h2>
          {topPages.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>No page data yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {topPages.map((p) => {
                const pct = Math.max(2, (p.value / topPages[0].value) * 100);
                return (
                  <div key={p.label}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.125rem" }}>
                      <span style={{ fontSize: "0.8125rem", fontWeight: 500, maxWidth: "70%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}</span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{p.value}</span>
                    </div>
                    <div style={{ height: "6px", background: "var(--neutral-100)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, var(--accent-400), var(--accent-600))", borderRadius: "var(--radius-full)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Referrers */}
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Traffic Sources</h2>
          {topReferrers.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>No referrer data yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {topReferrers.map((r) => {
                const pct = Math.max(2, (r.value / topReferrers[0].value) * 100);
                return (
                  <div key={r.label}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.125rem" }}>
                      <span style={{ fontSize: "0.8125rem", fontWeight: 500 }}>{r.label}</span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{r.value}</span>
                    </div>
                    <div style={{ height: "6px", background: "var(--neutral-100)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #0891b2, #06b6d4)", borderRadius: "var(--radius-full)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Countries */}
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Visitor Locations</h2>
          {topCountries.length === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>No geographic data yet. Country/region data is captured from visitor connections.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {topCountries.map((c) => {
                const pct = Math.max(2, (c.value / topCountries[0].value) * 100);
                return (
                  <div key={c.label}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.125rem" }}>
                      <span style={{ fontSize: "0.8125rem", fontWeight: 500, display: "flex", alignItems: "center", gap: "0.375rem" }}>
                        <Globe size={12} style={{ color: "var(--text-muted)" }} />{c.label}
                      </span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{c.value}</span>
                    </div>
                    <div style={{ height: "6px", background: "var(--neutral-100)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #059669, #34d399)", borderRadius: "var(--radius-full)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Hourly Traffic */}
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Visits by Hour</h2>
          {totalVisits === 0 ? (
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>No data yet.</p>
          ) : (
            <BarChart data={hourlyData.filter((d) => Number(d.label) % 3 === 0).map((d) => ({ label: `${d.label}:00`, value: d.value }))} color="#d97706" height={120} />
          )}
        </div>
      </div>

      {/* Device & Browser Breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "var(--space-4)", marginBottom: "var(--space-4)" }}>
        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Device Breakdown</h2>
          {deviceData.length > 0 ? <DonutChart data={deviceData} /> : <p className="text-muted" style={{ fontSize: "0.875rem" }}>No device data yet.</p>}
        </div>

        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Browser Breakdown</h2>
          {browserData.length > 0 ? <DonutChart data={browserData} /> : <p className="text-muted" style={{ fontSize: "0.875rem" }}>No browser data yet.</p>}
        </div>

        <div className="card" style={{ padding: "var(--space-4)" }}>
          <h2 style={{ fontSize: "1.125rem", marginBottom: "var(--space-3)" }}>Operating Systems</h2>
          {osList.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {osList.map((o) => {
                const pct = Math.max(2, (o.value / osList[0].value) * 100);
                return (
                  <div key={o.label}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.125rem" }}>
                      <span style={{ fontSize: "0.8125rem", fontWeight: 500 }}>{o.label}</span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{o.value}</span>
                    </div>
                    <div style={{ height: "6px", background: "var(--neutral-100)", borderRadius: "var(--radius-full)", overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #7c3aed, #a78bfa)", borderRadius: "var(--radius-full)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-muted" style={{ fontSize: "0.875rem" }}>No OS data yet.</p>}
        </div>
      </div>

      {/* Recent Visits Log */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "var(--space-4)", borderBottom: "1px solid var(--border)" }}>
          <h2 style={{ fontSize: "1.125rem" }}>Recent Visits</h2>
        </div>
        {visits.length === 0 ? (
          <div style={{ padding: "var(--space-6)", textAlign: "center" }}>
            <p className="text-muted" style={{ fontSize: "0.875rem" }}>No visits recorded yet. As people browse your site, their visits will appear here in real time.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto", maxHeight: "400px", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, background: "var(--bg-subtle)" }}>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  {["Time", "Page", "Location", "Device", "Browser", "Referrer"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visits.slice(0, 100).map((v) => (
                  <tr key={v.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "0.4rem 0.75rem", fontSize: "0.75rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{new Date(v.created_at).toLocaleString()}</td>
                    <td style={{ padding: "0.4rem 0.75rem", fontSize: "0.75rem", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px" }}>{v.page_path}</td>
                    <td style={{ padding: "0.4rem 0.75rem", fontSize: "0.75rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{[v.city, v.region, v.country].filter(Boolean).join(", ") || "—"}</td>
                    <td style={{ padding: "0.4rem 0.75rem", fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "capitalize", whiteSpace: "nowrap" }}>{v.device_type ?? "—"}</td>
                    <td style={{ padding: "0.4rem 0.75rem", fontSize: "0.75rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{v.browser ?? "—"}</td>
                    <td style={{ padding: "0.4rem 0.75rem", fontSize: "0.6875rem", color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "150px" }}>{v.referrer || "(direct)"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
